import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Search } from "lucide-react";
import DebtNavbar from "../components/layout/DebtNavbar";
import Footer from "../components/layout/Footer";
import Seo from "../components/Seo";
import api from "../lib/api";

/**
 * Public 114-lender directory grouped by lender type. Filterable by
 * category chip + free-text search — a marketing surface, not an
 * admin CRUD. The Admin `Lenders` page (private) remains the source
 * of truth for internal routing/matrix.
 */
export default function DebtBanksPage() {
  const [data, setData] = useState({ total: 0, by_type: [] });
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/public/lenders").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const src = active === "All" ? data.by_type : data.by_type.filter((g) => g.type === active);
    if (!q.trim()) return src;
    const needle = q.trim().toLowerCase();
    return src.map((g) => ({
      ...g,
      names: g.names.filter((n) => n.toLowerCase().includes(needle)),
    })).filter((g) => g.names.length > 0);
  }, [data, active, q]);

  return (
    <div className="min-h-screen text-ink bg-cream" data-testid="debt-banks-page">
      <Seo title="Our 114 Lender Partners · VenturaZ MONEY" description="PSU banks, private banks, NBFCs, HFCs, small finance banks — the full lender panel we tap for your loan." path="/venturaz-money/banks" />
      <DebtNavbar />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1F1408 0%, #3E2410 45%, #7A4E1D 100%)" }}>
        <div className="pointer-events-none absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full opacity-30" style={{ background: "radial-gradient(circle, #E9C46A 0%, transparent 60%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 py-14 lg:py-20 text-[#FFF3D9]">
          <div className="inline-flex items-center gap-1.5 bg-gold text-[#1F1408] text-[10px] font-mono uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full mb-4">
            {data.total || "114"} lenders · 8 categories · Curated
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight max-w-3xl mb-3">Every serious lender in India — on one page.</h1>
          <p className="text-[#FFF3D9]/80 text-lg leading-relaxed max-w-2xl">PSU banks. Private banks. NBFCs. Housing Finance Companies. Small Finance Banks. Foreign banks. Specialty NBFCs. Private-credit funds. We don&apos;t play favourites — we shortlist based on your case.</p>
        </div>
      </section>

      {/* Filters + directory */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="flex-1 flex flex-wrap gap-2" data-testid="debt-banks-filters">
            <button
              type="button"
              onClick={() => setActive("All")}
              className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border transition-colors ${active === "All" ? "bg-[#7A4E1D] text-[#FFF3D9] border-[#7A4E1D]" : "text-[#7A4E1D] border-[#B8860B]/40 hover:bg-[#7A4E1D]/10"}`}
              data-testid="debt-banks-filter-all"
            >
              All · {data.total || 0}
            </button>
            {data.by_type.map((g) => (
              <button
                key={g.type}
                type="button"
                onClick={() => setActive(g.type)}
                className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border transition-colors ${active === g.type ? "bg-[#7A4E1D] text-[#FFF3D9] border-[#7A4E1D]" : "text-[#7A4E1D] border-[#B8860B]/40 hover:bg-[#7A4E1D]/10"}`}
                data-testid={`debt-banks-filter-${g.type.replace(/\s+/g, "-")}`}
              >
                {g.type} · {g.count}
              </button>
            ))}
          </div>
          <div className="relative md:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              type="text"
              placeholder="Search lender…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-[#B8860B]/25 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#7A4E1D]"
              data-testid="debt-banks-search"
            />
          </div>
        </div>

        {groups.length === 0 && (
          <div className="text-sm text-ink-muted italic py-8" data-testid="debt-banks-empty">No lenders match that search.</div>
        )}

        {groups.map((g) => (
          <div key={g.type} className="mb-8" data-testid={`debt-banks-group-${g.type.replace(/\s+/g, "-")}`}>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-heading text-lg text-ink">{g.type}</h2>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{g.names.length} lender{g.names.length === 1 ? "" : "s"}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {g.names.map((name) => (
                <div key={name} className="border border-[#B8860B]/20 rounded-lg px-3 py-2 bg-white text-[13px] font-heading text-ink truncate flex items-center gap-1.5" data-testid={`debt-bank-${name.replace(/\s+/g, "-")}`}>
                  <Landmark size={11} className="text-[#7A4E1D] shrink-0" />
                  <span className="truncate">{name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-10 rounded-2xl border border-[#B8860B]/25 p-6 bg-white flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1">
            <div className="font-heading text-ink text-lg mb-1">Not sure which lender fits your case?</div>
            <div className="text-[13px] text-ink-soft">We do the shortlisting for free — bureau pull, CAM-Lite, negotiation, all included.</div>
          </div>
          <Link to="/money" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-[#7A4E1D] text-[#FFF3D9] text-sm font-heading font-bold hover:bg-[#5D3B15] transition-colors" data-testid="debt-banks-cta">
            Start free consultation
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
